import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../Button';
import { useServices } from '../../../hooks/useServices';
import { useSettings } from '../../../hooks/useSettings';
import './Header.css';
import logo from '../../../assets/images/Logo.webp';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const { services } = useServices();
  const { settings } = useSettings();

  const getShortName = (name) => {
    if (name.includes('Intuitive Reiki — Tune')) return 'Reiki - Tune & Restore';
    if (name.includes('Intuitive Reiki — Intro')) return 'Reiki - Intro & Integrate';
    if (name.includes('Deep Release')) return 'Deep Release & Rebalance';
    if (name.includes('Tarot')) return 'Tarot Readings';
    if (name.includes('Soul Alignment')) return 'Soul Alignment Coaching';
    if (name.includes('Chakra')) return 'Chakra Healing';
    if (name.includes('Space')) return 'Space Clearing';
    if (name.includes('Restorative')) return 'Restorative Reiki Classes';
    if (name.includes('Guided Ascension')) return 'Guided Ascension Program';
    return name;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="header__wrapper">
        <nav className="header__nav">
          <Link to="/" className="header__logo">
            <img
              src={logo}
              alt={settings?.businessName || 'KO Holistic Health & Wellbeing'}
              className="header__logo-img"
            />
            <div className="header__logo-brand">
              <span className="header__logo-name">KO Holistic</span>
              <span className="header__logo-name">Health</span>
              <span className="header__logo-tagline">& Wellbeing</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <ul className="header__menu">
            <li>
              <Link to="/" className={isActive('/') ? 'active' : ''}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/about" className={isActive('/about') ? 'active' : ''}>
                About
              </Link>
            </li>
            <li className="header__menu-dropdown"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className="header__menu-dropdown-trigger"
                onClick={toggleServices}
                aria-expanded={isServicesOpen}
              >
                Services
                <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <ul className={`header__dropdown ${isServicesOpen ? 'header__dropdown--open' : ''}`}>
                {services.map((service) => (
                  <li key={service.id}>
                    <Link to={`/services/${service.slug}`}>{getShortName(service.name)}</Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Link to="/events" className={isActive('/events') ? 'active' : ''}>
                Events
              </Link>
            </li>
            <li>
              <Link to="/courses" className={isActive('/courses') ? 'active' : ''}>
                Training
              </Link>
            </li>
            <li>
              <Link to="/speaker" className={isActive('/speaker') ? 'active' : ''}>
                Speaker
              </Link>
            </li>
            <li>
              <Link to="/gallery" className={isActive('/gallery') ? 'active' : ''}>
                Gallery
              </Link>
            </li>
            <li>
              <Link to="/blog" className={isActive('/blog') ? 'active' : ''}>
                Blog
              </Link>
            </li>
            <li>
              <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>
                Contact
              </Link>
            </li>
          </ul>

          <div className="header__actions">
            <a
              href={`tel:${settings?.phoneFormatted || '+61439386551'}`}
              className="header__phone"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span>{settings?.phone || '0439 386 551'}</span>
            </a>
            <Button
              href={settings?.bookingLink || '/booking'}
              size="medium"
              className="header__cta"
            >
              {settings?.bookingText || 'Book Now'}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={`header__hamburger ${isMenuOpen ? 'header__hamburger--open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </nav>

        {/* Mobile Navigation */}
        <div className={`header__mobile-menu ${isMenuOpen ? 'header__mobile-menu--open' : ''}`}>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/about">About</Link></li>
            <li className="header__mobile-submenu">
              <button onClick={toggleServices}>
                Services
                <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor">
                  <path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" />
                </svg>
              </button>
              {isServicesOpen && (
                <ul className="header__mobile-submenu-list">
                  {services.map((service) => (
                    <li key={service.id}>
                      <Link to={`/services/${service.slug}`}>{getShortName(service.name)}</Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li><Link to="/events">Events</Link></li>
            <li><Link to="/courses">Training</Link></li>
            <li><Link to="/speaker">Speaker</Link></li>
            <li><Link to="/gallery">Gallery</Link></li>
            <li><Link to="/blog">Blog</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li className="header__mobile-cta">
              <a
                href={`tel:${settings?.phoneFormatted || '+61439386551'}`}
                className="header__mobile-phone"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>{settings?.phone || '0439 386 551'}</span>
              </a>
              <Button
                href={settings?.bookingLink || '/booking'}
                fullWidth
              >
                {settings?.bookingText || 'Book Now'}
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
